import { useEffect, useMemo, useState } from "react";
import {
  Modal,
  Button,
  Tag,
  Form,
  InputNumber,
  Select,
  Divider,
  Spin,
  Typography,
  Empty,
  Popover,
  Progress,
  notification,
  FormInstance,
  message,
} from "antd";
import {
  LeftOutlined,
  CloseOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
// import { negotiationOfferService } from "./negotiation-offer.service";
import {
  T_NegotiationOffer,
  T_NegotiationContext,
  T_SavedCustomOffer,
  T_CustomOfferFormValues,
  T_CustomOfferResult,
  T_OfferDisplayDetail,
  T_ClientGainMetric,
  T_ClientGainLegacy,
  isClientGainMetrics,
  T_AcceptanceLevel,
  T_OfferType,
} from "dtos/smart-negotiation.dto";
import { ApiService } from "api/api.service";
import { formatDate, formatMoney } from "utils/functions";
import { IoIosArrowBack } from "react-icons/io";

const { Text, Title } = Typography;

const OFFER_TYPE_LABELS: Record<T_OfferType, string> = {
  settlement: "Settlement",
  restructure: "Restructure",
  combined: "Combined",
  deferral: "Deferral",
  discounted_frequency_change: "Discounted / New Schedule",
};

const CONTEXT_FIELDS = ["max_settlement_amount", "max_weekly_amount", "total_weeks", "deferral_weeks"] as const;

const CONTEXT_FIELD_LABELS: Record<(typeof CONTEXT_FIELDS)[number], string> = {
  max_settlement_amount: "Max settlement amount",
  max_weekly_amount: "Max weekly amount",
  total_weeks: "Total weeks",
  deferral_weeks: "Deferral weeks",
};

const ACCEPTANCE_COLOR: Record<T_AcceptanceLevel, { bg: string; text: string; border: string }> = {
  High: { bg: "#F0FDF4", text: "#16A34A", border: "#BBF7D0" },
  Medium: { bg: "#FFFBEB", text: "#D97706", border: "#FDE68A" },
  Low: { bg: "#FEF2F2", text: "#DC2626", border: "#FECACA" },
};

type T_GainDirection = "improved" | "declined" | "neutral";

/** Direction is derived from change_pct's sign (falling back to the
 *  before/after booleans) rather than assumed — a metric can get worse. */
function getGainDirection(metric: T_ClientGainMetric): T_GainDirection {
  if (metric.change_pct != null) {
    if (metric.change_pct > 0.5) return "improved";
    if (metric.change_pct < -0.5) return "declined";
    return "neutral";
  }
  if (metric.unit === "boolean" && metric.before === true && metric.after === false) return "improved";
  if (metric.unit === "boolean" && metric.before === false && metric.after === true) return "declined";
  return "neutral";
}

/** Reuses the existing ACCEPTANCE_COLOR green/red tokens so "good vs bad"
 *  reads the same way here as it does for acceptance level elsewhere. */
function getGainDirectionColor(direction: T_GainDirection): { text: string; bar: string } {
  if (direction === "improved") return { text: ACCEPTANCE_COLOR.High.text, bar: ACCEPTANCE_COLOR.High.text };
  if (direction === "declined") return { text: ACCEPTANCE_COLOR.Low.text, bar: ACCEPTANCE_COLOR.Low.text };
  return { text: "#6f4fef", bar: "#6f4fef" };
}

function scaleGainBar(before: number, after: number): { beforePct: number; afterPct: number } | null {
  if (!Number.isFinite(before) || !Number.isFinite(after) || before < 0 || after < 0) return null;
  const max = Math.max(before, after);
  if (max <= 0) return null;
  return { beforePct: (before / max) * 100, afterPct: (after / max) * 100 };
}

const NUMERIC_GAIN_UNITS = new Set(["currency", "currency_per_week", "weeks"]);

const CURRENCY_LABEL_HINT = /amount|payment|value|recovery|deposit|settlement|discount|paid|owed/i;
const COUNT_LABEL_HINT = /week|month|period|day/i;

function formatBreakdownValue(label: string, value: string | number): string {
  if (typeof value === "string") return value;
  if (value == null) return "—";
  if (CURRENCY_LABEL_HINT.test(label)) return formatMoney(value);
  if (COUNT_LABEL_HINT.test(label)) return Number.isInteger(value) ? String(value) : value.toFixed(1);
  return value.toLocaleString("en-US");
}

function formatMetricValue(unit: string, value: number | boolean | null) {
  if (value == null) return "—";
  if (unit === "currency") return formatMoney(value as number);
  if (unit === "currency_per_week") return `${formatMoney(value as number)}/wk`;
  if (unit === "weeks") return `${Math.round(value as number)} wks`;
  if (unit === "boolean") return value ? "Yes" : "No";
  return String(value);
}

function normalizeGeneratedOffer(offer: T_NegotiationOffer): T_OfferDisplayDetail {
  return {
    summary: offer.generated_offer_detail.summary,
    breakdown: offer.generated_offer_detail.breakdown,
    acceptance_level: offer.acceptance_level,
    reason: offer.reason,
    calculated_gap: offer.calculated_gap,
    client_gain: offer.client_gain,
    select_when: offer.select_when,
    dont_select_when: offer.dont_select_when,
  };
}

function normalizeCustomResult(result: T_CustomOfferResult): T_OfferDisplayDetail {
  return {
    summary: result.generated_customized_offer.summary,
    breakdown: result.generated_customized_offer.breakdown,
    acceptance_level: result.acceptance_level,
    reason: result.reason,
    calculated_gap: result.calculated_gap,
    client_gain: result.client_gain,
  };
}

/** Best-effort mapping from a generated offer's `terms` onto the customize
 *  form. Confirmed against a real sample for deferral / restructure /
 *  discounted_frequency_change. Settlement and combined are inferred from
 *  the same naming convention — double check once you can hit those types
 *  live. Falls back to 0 on any missing field rather than throwing. */
function mapOfferToCustomForm(offer: T_NegotiationOffer): T_CustomOfferFormValues {
  const t = offer.terms || {};
  switch (offer.offer_type) {
    case "settlement":
      return {
        lump_sum: t.amount ?? 0,
        periodic_amount: 0,
        total_period: 0,
        payment_frequency: "weekly",
        deferral_weeks: 0,
      };
    case "restructure":
      return {
        lump_sum: 0,
        periodic_amount: t.amount ?? 0,
        total_period: t.weeks ?? 0,
        payment_frequency: "weekly",
        deferral_weeks: 0,
      };
    case "combined":
      return {
        lump_sum: t.upfront_amount ?? 0,
        periodic_amount: t.weekly_amount ?? 0,
        total_period: t.weeks ?? 0,
        payment_frequency: "weekly",
        deferral_weeks: 0,
      };
    case "deferral":
      return {
        lump_sum: 0,
        periodic_amount: t.post_deferral_weekly_amount ?? 0,
        total_period: t.remaining_weeks_after_deferral ?? 0,
        payment_frequency: "weekly",
        deferral_weeks: t.deferral_weeks ?? 0,
      };
    case "discounted_frequency_change":
      return {
        lump_sum: 0,
        periodic_amount: t.periodic_amount ?? 0,
        total_period: t.total_periods ?? 0,
        payment_frequency: t.frequency ?? "weekly",
        deferral_weeks: 0,
      };
    default:
      return { lump_sum: 0, periodic_amount: 0, total_period: 0, payment_frequency: "weekly", deferral_weeks: 0 };
  }
}

function mapSavedOfferToCustomForm(offer: T_SavedCustomOffer): T_CustomOfferFormValues {
  return {
    lump_sum: offer.lump_sum,
    periodic_amount: offer.periodic_amount,
    total_period: offer.total_period,
    payment_frequency: offer.payment_frequency,
    deferral_weeks: offer.deferral_weeks,
  };
}

function needsContextSetup(ctx: T_NegotiationContext): boolean {
  return Object.values(ctx.context_field_sources).some((s) => s === "estimated");
}

type T_Props = {
  open: boolean;
  onClose: () => void;
  loanId: string;
  application_id: string;
  thread_id?: string;
  message_id?: string;
};

type T_Selected =
  | { source: "generated"; offer: T_NegotiationOffer; index: number }
  | { source: "saved"; offer: T_SavedCustomOffer }
  | null;

type T_Phase = "loading-context" | "context-form" | "generating" | "list" | "customize";

export default function GenerateOfferModal({ open, onClose, loanId, application_id, message_id, thread_id }: T_Props) {
  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      closable={false}
      width={1180}
      styles={{ body: { padding: 0, height: 720, maxHeight: "88vh" } }}
    >
      {open && (
        <GenerateOfferModalBody
          loanId={loanId}
          application_id={application_id}
          onClose={onClose}
          message_id={message_id}
          thread_id={thread_id}
        />
      )}
    </Modal>
  );
}

function GenerateOfferModalBody({ loanId, application_id, onClose, message_id, thread_id }: Omit<T_Props, "open">) {
  const [notifyApi, notifyHolder] = notification.useNotification();

  const [phase, setPhase] = useState<T_Phase>("loading-context");
  const [contextFormMode, setContextFormMode] = useState<"setup" | "edit">("setup");
  const [context, setContext] = useState<T_NegotiationContext | null>(null);
  const [offers, setOffers] = useState<T_NegotiationOffer[]>([]);
  const [uncertaintyNote, setUncertaintyNote] = useState<string | null>(null);
  const [uncertaintyDismissed, setUncertaintyDismissed] = useState(false);
  const [selected, setSelected] = useState<T_Selected>(null);
  const [rightCollapsed, setRightCollapsed] = useState(false);
  const [customBasedOn, setCustomBasedOn] = useState<string | null>(null);
  const [previewResult, setPreviewResult] = useState<T_CustomOfferResult | null>(null);
  const [savedFlag, setSavedFlag] = useState(false);
  const [customForm] = Form.useForm<T_CustomOfferFormValues>();

  const contextQuery = ApiService.negotiation.useGetContext({ application_id, loan_id: loanId });
  const upsertContextMutation = ApiService.negotiation.useUpsertContext();
  const generateOffersMutation = ApiService.negotiation.useGenerateOffers();
  const previewMutation = ApiService.negotiation.usePreviewCustomOffer();
  const saveMutation = ApiService.negotiation.useSaveCustomOffer();
  const savedOffersQuery = ApiService.negotiation.useSavedCustomOffers({
    application_id,
    loan_id: loanId,
    message_id,
    thread_id,
  });

  const baseRequest = { application_id, loan_id: loanId, thread_id, message_id };
  console.log({ baseRequest });

  const runGenerate = () => {
    setPhase("generating");
    generateOffersMutation.mutate(
      { body: baseRequest },
      {
        onSuccess: (res) => {
          const data = res.result; // TODO: adjust if T_Api_Success_Res doesn't nest the payload under `.result`
          setOffers(data.offers);
          setUncertaintyNote(data.context_uncertainty_note);
          setUncertaintyDismissed(false);
          setSelected(data.offers.length ? { source: "generated", offer: data.offers[0], index: 0 } : null);
          setPhase("list");
        },
        onError: () => {
          // notifyApi.error({ message: "Couldn't generate offers", description: "Please try again." });
          setPhase(offers.length ? "list" : "context-form");
        },
      },
    );
  };

  useEffect(() => {
    if (!contextQuery.isSuccess) return;
    const ctx = contextQuery.data.result;
    setContext(ctx);
    if (needsContextSetup(ctx)) {
      setContextFormMode("setup");
      setPhase("context-form");
    } else {
      runGenerate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextQuery.isSuccess]);

  useEffect(() => {
    if (!contextQuery.isError) return;
    // No context yet, or the request failed — let the user set it up (or skip).
    setContextFormMode("setup");
    setPhase("context-form");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextQuery.isError]);

  const handleContextSave = (values: Partial<Record<(typeof CONTEXT_FIELDS)[number], number>>) => {
    upsertContextMutation.mutate(
      { body: { ...baseRequest, ...values } },
      {
        onSuccess: (res) => {
          setContext(res.result); // TODO: adjust if unwrapped differently
          runGenerate();
        },
        // onError: () => notifyApi.error({ message: "Couldn't save context", description: "Please try again." }),
      },
    );
  };

  const handleProceedWithoutContext = () => {
    if (offers.length > 0) {
      // Reached via "Add contexts" on an existing list — just go back, nothing changed.
      setPhase("list");
    } else {
      runGenerate();
    }
  };

  const handleAddContexts = () => {
    setContextFormMode("edit");
    setPhase("context-form");
  };

  const handleSelectOffer = (offer: T_NegotiationOffer, index: number) =>
    setSelected({ source: "generated", offer, index });
  const handleSelectSaved = (offer: T_SavedCustomOffer) => setSelected({ source: "saved", offer });

  const handleStartCustomize = () => {
    if (!selected) return;
    if (selected.source === "generated") {
      customForm.setFieldsValue(mapOfferToCustomForm(selected.offer));
      setCustomBasedOn(OFFER_TYPE_LABELS[selected.offer.offer_type]);
    } else {
      customForm.setFieldsValue(mapSavedOfferToCustomForm(selected.offer));
      setCustomBasedOn("saved custom offer");
    }
    setPreviewResult(null);
    setSavedFlag(false);
    setPhase("customize");
  };

  const handleBackToList = () => {
    setPreviewResult(null);
    setSavedFlag(false);
    setPhase("list");
  };

  const handlePreview = async () => {
    const values = await customForm.validateFields();
    previewMutation.mutate(
      { body: { ...baseRequest, ...values } },
      {
        onSuccess: (res) => {
          setPreviewResult(res.result); // TODO: adjust if unwrapped differently
          setSavedFlag(false);
        },
        // onError: () => notifyApi.error({ message: "Couldn't preview offer", description: "Please try again." }),
      },
    );
  };

  const handleSaveCustom = () => {
    const values = customForm.getFieldsValue();
    saveMutation.mutate(
      { body: { ...baseRequest, ...values } },
      {
        onSuccess: (res) => {
          setPreviewResult(res.result); // TODO: adjust if unwrapped differently
          setSavedFlag(true);
          message.success("Custom offer saved");
        },
        // onError: () => notifyApi.error({ message: "Couldn't save offer", description: "Please try again." }),
      },
    );
  };

  const selectedDetail: T_OfferDisplayDetail | null = useMemo(() => {
    if (!selected) return null;
    return selected.source === "generated"
      ? normalizeGeneratedOffer(selected.offer)
      : normalizeCustomResult(selected.offer);
  }, [selected]);

  const rightPanelDetail: T_OfferDisplayDetail | null = useMemo(() => {
    if (phase === "customize" && previewResult) return normalizeCustomResult(previewResult);
    return selectedDetail;
  }, [phase, previewResult, selectedDetail]);

  const showAddContexts = phase === "list";
  const showPanelToggle = phase === "list" || phase === "customize";
  const showBody3Col = phase === "list" || phase === "customize";

  return (
    <div className="flex flex-col h-full">
      {notifyHolder}

      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
        <Text className="text-[15px] font-semibold">Generate Offer</Text>
        <div className="flex items-center gap-2">
          {showAddContexts && (
            <Button size="small" onClick={handleAddContexts}>
              {contextQuery.data?.result && needsContextSetup(contextQuery.data?.result)
                ? `Add Contexts`
                : "Update Contexts"}
            </Button>
          )}
          {/* {showPanelToggle && (
            <Button size="small" onClick={() => setRightCollapsed((v) => !v)}>
              {rightCollapsed ? "Show panel" : "Hide panel"}
            </Button>
          )} */}
          <Button type="text" size="small" icon={<CloseOutlined />} onClick={onClose} />
        </div>
      </div>

      {/* Uncertainty banner */}
      {showBody3Col && uncertaintyNote && !uncertaintyDismissed && (
        <div className="flex items-center justify-between gap-3 px-5 py-2.5 bg-amber-50 border-b border-amber-100 flex-shrink-0">
          <div className="flex items-center gap-2 text-xs text-amber-700">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" />
            <span>{uncertaintyNote}</span>
          </div>
          <Button type="text" size="small" onClick={() => setUncertaintyDismissed(true)}>
            ×
          </Button>
        </div>
      )}

      {/* Body */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {phase === "loading-context" && <CenteredSpinner label="Loading case details…" />}

        {phase === "context-form" && (
          <ContextFormView
            mode={contextFormMode}
            initial={context}
            saving={upsertContextMutation.isPending}
            onSave={handleContextSave}
            onSkip={handleProceedWithoutContext}
          />
        )}

        {phase === "generating" && <CenteredSpinner label="Generating offers…" />}

        {showBody3Col && (
          <div className="flex h-full">
            <OfferListSidebar
              phase={phase}
              offers={offers}
              selected={selected}
              savedOffers={savedOffersQuery.data?.result ?? []}
              savedLoading={savedOffersQuery.isLoading}
              onSelectOffer={handleSelectOffer}
              onSelectSaved={handleSelectSaved}
              onBack={handleBackToList}
              basedOnLabel={customBasedOn}
            />

            <div className="flex-1 min-w-0 px-6 py-5 overflow-y-auto">
              {phase === "list" ? (
                <OfferDetailPane detail={selectedDetail} hasSelection={!!selected} onCustomize={handleStartCustomize} />
              ) : (
                <CustomizeView
                  form={customForm}
                  previewing={previewMutation.isPending}
                  saving={saveMutation.isPending}
                  saved={savedFlag}
                  previewResult={previewResult}
                  onPreview={handlePreview}
                  onSave={handleSaveCustom}
                  onFormChange={() => setSavedFlag(false)}
                />
              )}
            </div>

            {!rightCollapsed && (
              <div className="w-[350px] flex-shrink-0 border-0 border-l border-solid border-gray-200 px-4 py-5 overflow-y-auto">
                <GainPanel detail={rightPanelDetail} />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function CenteredSpinner({ label }: { label: string }) {
  return (
    <div className="h-full flex flex-col items-center justify-center gap-3">
      <Spin size="large" />
      <Text type="secondary" className="text-xs">
        {label}
      </Text>
    </div>
  );
}

function ContextFormView({
  mode,
  initial,
  saving,
  onSave,
  onSkip,
}: {
  mode: "setup" | "edit";
  initial: T_NegotiationContext | null;
  saving: boolean;
  onSave: (values: Partial<Record<(typeof CONTEXT_FIELDS)[number], number>>) => void;
  onSkip: () => void;
}) {
  const [form] = Form.useForm();

  useEffect(() => {
    form.setFieldsValue({
      max_settlement_amount: initial?.max_settlement_amount,
      max_weekly_amount: initial?.max_weekly_amount,
      total_weeks: initial?.total_weeks,
      deferral_weeks: initial?.deferral_weeks,
    });
  }, [initial]);

  return (
    <div className="h-full overflow-y-auto px-8 py-8 flex justify-center">
      <div className="w-full max-w-[440px]">
        <div className="flex items-center gap-1 relative right-24">
          <Button onClick={onSkip} type="text" icon={<IoIosArrowBack />}>
            Back
          </Button>
          <Title level={5} className="!mb-1">
            Add contexts
          </Title>
        </div>
        <Text type="secondary" className="text-xs block mb-5">
          {mode === "setup"
            ? "Optional case limits. Nothing here is required — you can proceed without setting any of it."
            : "Optional case limits. Fields left unchanged keep their current value."}
        </Text>

        <Form form={form} layout="vertical" onFinish={onSave}>
          {CONTEXT_FIELDS.map((f) => (
            <Form.Item
              key={f}
              name={f}
              label={
                <div className="flex justify-between w-full">
                  <span>{CONTEXT_FIELD_LABELS[f]}</span>
                  {/* {initial?.context_field_sources?.[f] && (
                    <span className="text-[11px] text-gray-400">{initial.context_field_sources[f]}</span>
                  )} */}
                </div>
              }
            >
              <InputNumber className="w-full" min={0} />
            </Form.Item>
          ))}

          <Text type="secondary" className="text-[11px] block mb-5">
            Lowering the weekly ceiling significantly can drop a payment-based offer entirely if it can no longer
            recover a meaningful share of the balance.
          </Text>

          <div className="flex justify-end gap-2">
            <Button onClick={onSkip}>Proceed without adding context</Button>
            <Button type="primary" htmlType="submit" loading={saving}>
              Save
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
}

function OfferListSidebar({
  phase,
  offers,
  selected,
  savedOffers,
  savedLoading,
  onSelectOffer,
  onSelectSaved,
  onBack,
  basedOnLabel,
}: {
  phase: T_Phase;
  offers: T_NegotiationOffer[];
  selected: T_Selected;
  savedOffers: T_SavedCustomOffer[];
  savedLoading: boolean;
  onSelectOffer: (offer: T_NegotiationOffer, index: number) => void;
  onSelectSaved: (offer: T_SavedCustomOffer) => void;
  onBack: () => void;
  basedOnLabel: string | null;
}) {
  return (
    <div className="w-[240px] flex-shrink-0 border-0 border-r border-solid border-gray-200 px-3 py-4 overflow-y-auto flex flex-col gap-2.5">
      {phase === "customize" ? (
        <>
          <Button size="small" icon={<LeftOutlined />} onClick={onBack} className="self-start">
            Back
          </Button>
          {basedOnLabel && (
            <Text type="secondary" className="text-xs px-1.5">
              Based on {basedOnLabel}
            </Text>
          )}
        </>
      ) : (
        <>
          <Text className="text-[11px] tracking-wide uppercase text-gray-400 px-1.5">Offers</Text>

          {offers.length === 0 ? (
            <Text type="secondary" className="text-xs px-1.5">
              No viable offers with the current limits. Try raising the weekly or settlement ceiling in Add contexts.
            </Text>
          ) : (
            <div className="flex flex-col gap-1.5">
              {offers.map((offer, i) => {
                const isSelected = selected?.source === "generated" && selected.index === i;
                const c = ACCEPTANCE_COLOR[offer.acceptance_level];
                return (
                  <div
                    key={i}
                    onClick={() => onSelectOffer(offer, i)}
                    className={`cursor-pointer rounded-lg px-2.5 py-2 border border-solid ${
                      isSelected ? "bg-purple-50 border-purple-200" : "border-transparent hover:bg-gray-50"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <Text className="text-[13px] font-semibold">{OFFER_TYPE_LABELS[offer.offer_type]}</Text>
                      <Tag style={{ margin: 0, fontSize: 10, background: c.bg, color: c.text, borderColor: c.border }}>
                        {offer.acceptance_level}
                      </Tag>
                    </div>
                    {/* <Text type="secondary" className="text-xs block mt-0.5 line-clamp-2">
                      {offer.generated_offer_detail.summary}
                    </Text> */}
                  </div>
                );
              })}
            </div>
          )}

          <Divider className="my-1.5" />
          <Text className="text-[11px] tracking-wide uppercase text-gray-400 px-1.5">Saved offers</Text>

          {savedLoading ? (
            <Spin size="small" className="ml-1.5" />
          ) : savedOffers.length === 0 ? (
            <Text type="secondary" className="text-xs px-1.5">
              No custom offers saved yet.
            </Text>
          ) : (
            <div className="flex flex-col gap-1.5">
              {savedOffers.map((s, idx) => {
                const isSelected = selected?.source === "saved" && selected.offer.id === s.id;
                return (
                  <div
                    key={s.id}
                    onClick={() => onSelectSaved(s)}
                    className={`cursor-pointer rounded-lg px-2.5 py-2 border border-solid ${
                      isSelected ? "bg-purple-50 border-purple-200" : "border-transparent hover:bg-gray-50"
                    }`}
                  >
                    <Text className="text-[13px] font-semibold">{`Custom offer #${savedOffers.length - idx}`}</Text>
                    {/* <Text type="secondary" className="text-xs block mt-0.5 line-clamp-2">
                      {s.generated_customized_offer.summary}
                    </Text> */}
                    {s.saved_at && (
                      <Text type="secondary" className="text-[10px] block mt-0.5">
                        Saved on {formatDate(s?.saved_at)}
                      </Text>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function OfferDetailPane({
  detail,
  hasSelection,
  onCustomize,
}: {
  detail: T_OfferDisplayDetail | null;
  hasSelection: boolean;
  onCustomize: () => void;
}) {
  if (!hasSelection || !detail) {
    return <Empty description="Select an offer to see details" className="mt-10" />;
  }

  return (
    <div className="max-w-[540px]">
      <Text className="text-[17px] block font-semibold">{detail.summary}</Text>

      <Text className="text-[11px] tracking-wide uppercase text-gray-400 block mt-5 mb-1.5">Offer breakdown</Text>
      <div className="flex flex-col">
        {Object.entries(detail.breakdown).map(([label, value]) => (
          <div key={label} className="flex justify-between gap-3 py-2.5 border-0 border-b border-solid border-gray-200">
            <Text type="secondary" className="text-[13px]">
              {label}
            </Text>
            <Text className="text-[13px] font-semibold whitespace-nowrap">{formatBreakdownValue(label, value)}</Text>
          </div>
        ))}
      </div>

      {(detail.select_when || detail.dont_select_when) && (
        <div className="mt-6 flex flex-col gap-3">
          {detail.select_when && (
            <div
              className="rounded-md border-0 border-l-[3px] border-solid pl-3 pr-3 py-2.5"
              style={{ borderLeftColor: ACCEPTANCE_COLOR.High.text, background: ACCEPTANCE_COLOR.High.bg }}
            >
              <div className="flex items-center gap-1.5 mb-1">
                <CheckCircleOutlined style={{ color: ACCEPTANCE_COLOR.High.text, fontSize: 12 }} />
                <Text
                  className="text-[11px] font-semibold tracking-wide uppercase"
                  style={{ color: ACCEPTANCE_COLOR.High.text }}
                >
                  When to select
                </Text>
              </div>
              <Text className="text-[13px] leading-relaxed">{detail.select_when}</Text>
            </div>
          )}
          {detail.dont_select_when && (
            <div
              className="rounded-md border-0 border-l-[3px] border-solid pl-3 pr-3 py-2.5"
              style={{ borderLeftColor: ACCEPTANCE_COLOR.Low.text, background: ACCEPTANCE_COLOR.Low.bg }}
            >
              <div className="flex items-center gap-1.5 mb-1">
                <CloseCircleOutlined style={{ color: ACCEPTANCE_COLOR.Low.text, fontSize: 12 }} />
                <Text
                  className="text-[11px] font-semibold tracking-wide uppercase"
                  style={{ color: ACCEPTANCE_COLOR.Low.text }}
                >
                  When not to select
                </Text>
              </div>
              <Text className="text-[13px] leading-relaxed">{detail.dont_select_when}</Text>
            </div>
          )}
        </div>
      )}

      <Button type="primary" className="mt-6" onClick={onCustomize}>
        Customize this offer
      </Button>
    </div>
  );
}

function CustomizeView({
  form,
  previewing,
  saving,
  saved,
  previewResult,
  onPreview,
  onSave,
  onFormChange,
}: {
  form: FormInstance<T_CustomOfferFormValues>;
  previewing: boolean;
  saving: boolean;
  saved: boolean;
  previewResult: T_CustomOfferResult | null;
  onPreview: () => void;
  onSave: () => void;
  onFormChange: () => void;
}) {
  return (
    <div className="max-w-[520px] flex flex-col gap-4">
      <Form form={form} layout="vertical" onValuesChange={onFormChange}>
        <div className="grid grid-cols-2 gap-4">
          <Form.Item name="lump_sum" label="Down payment">
            <InputNumber className="w-full" min={0} />
          </Form.Item>
          <Form.Item name="periodic_amount" label="Periodic amount">
            <InputNumber className="w-full" min={0} />
          </Form.Item>
          <Form.Item name="total_period" label="Number of periods">
            <InputNumber className="w-full" min={0} />
          </Form.Item>
          <Form.Item name="payment_frequency" label="Frequency">
            <Select
              options={[
                { value: "daily", label: "Daily" },
                { value: "weekly", label: "Weekly" },
                { value: "monthly", label: "Monthly" },
              ]}
            />
          </Form.Item>
          <Form.Item name="deferral_weeks" label="Deferral weeks">
            <InputNumber className="w-full" min={0} />
          </Form.Item>
        </div>

        <Button onClick={onPreview} loading={previewing}>
          Preview offer
        </Button>
      </Form>

      {previewResult && (
        <div className="mt-2 pt-4 border-0 border-t border-solid border-gray-200 flex flex-col gap-3.5">
          <Text className="text-[11px] tracking-wide uppercase text-gray-400">Revised customized offer</Text>
          <Text className="text-[15px] leading-relaxed">{previewResult.generated_customized_offer.summary}</Text>
          <div className="flex flex-col">
            {Object.entries(previewResult.generated_customized_offer.breakdown).map(([label, value]) => (
              <div
                key={label}
                className="flex justify-between gap-3 py-2 border-0 border-b border-solid border-gray-200"
              >
                <Text type="secondary" className="text-[13px]">
                  {label}
                </Text>
                <Text className="text-[13px] font-semibold whitespace-nowrap">
                  {formatBreakdownValue(label, value)}
                </Text>
              </div>
            ))}
          </div>
          <Button type={saved ? "default" : "primary"} onClick={onSave} loading={saving} className="self-start">
            {saved ? "Saved ✓" : "Save customized offer"}
          </Button>
        </div>
      )}
    </div>
  );
}

function GainPanel({ detail }: { detail: T_OfferDisplayDetail | null }) {
  if (!detail) return null;

  const gap = detail.calculated_gap != null ? `${Math.round(detail.calculated_gap * 100)}%` : "—";

  const acceptanceColor = ACCEPTANCE_COLOR[detail.acceptance_level];

  return (
    <div>
      <div className="flex items-stretch gap-2">
        <div
          className="flex-1 rounded-md px-2.5 py-1.5 border border-solid"
          style={{ background: acceptanceColor.bg, borderColor: acceptanceColor.border }}
        >
          <Text className="block text-[10px] leading-tight font-medium" style={{ color: acceptanceColor.text }}>
            Acceptance
          </Text>
          <Text strong className="text-sm leading-tight block" style={{ color: acceptanceColor.text }}>
            {detail.acceptance_level}
          </Text>
        </div>
        <div className="flex-1 rounded-md bg-zinc-50 border border-solid border-zinc-100 px-2.5 py-1.5">
          <div className="flex items-center gap-1">
            <Text className="block text-[10px] text-zinc-400 leading-tight font-medium">Offer Variance</Text>
            <Popover content="How far this offer sits from the creditor's last counter-offer. Lower is closer.">
              <InfoCircleOutlined className="text-[10px] text-gray-300" />
            </Popover>
          </div>
          <Text strong className="text-sm leading-tight block">
            {gap}
          </Text>
        </div>
      </div>

      <Text type="secondary" className="text-[13px] leading-relaxed block mt-3.5">
        {detail.reason}
      </Text>

      <Text className="text-[11px] tracking-wide uppercase text-gray-400 block mt-4 pt-3.5 border-0 border-t border-solid border-gray-200 mb-1">
        Client gain
      </Text>
      <div className="flex flex-col gap-3.5">
        {isClientGainMetrics(detail.client_gain) ? (
          detail.client_gain.metrics.length ? (
            detail.client_gain.metrics.map((m) => <GainMetricRow key={m.key} metric={m} />)
          ) : (
            <Text type="secondary" className="text-xs">
              No comparison data available for this offer.
            </Text>
          )
        ) : (
          <LegacyGainFallback gain={detail.client_gain} />
        )}
      </div>
    </div>
  );
}

function GainMetricRow({ metric }: { metric: T_ClientGainMetric }) {
  const direction = getGainDirection(metric);
  const color = getGainDirectionColor(direction);

  const badge =
    metric.change_pct != null && direction !== "neutral" ? (
      <Tag color={direction === "improved" ? "success" : "error"} style={{ fontSize: 11, margin: 0 }}>
        {Math.abs(Math.round(metric.change_pct))}% {direction === "improved" ? "better" : "worse"}
      </Tag>
    ) : null;

  const isNumeric =
    NUMERIC_GAIN_UNITS.has(metric.unit) && typeof metric.before === "number" && typeof metric.after === "number";
  const bars = isNumeric ? scaleGainBar(metric.before as number, metric.after as number) : null;

  return (
    <div className="flex flex-col gap-1.5 rounded-md bg-gray-50/60 border border-solid border-gray-100 px-2.5 py-2">
      <div className="flex items-center justify-between gap-2">
        <Text type="secondary" className="text-xs">
          {metric.label}
        </Text>
        {badge}
      </div>

      {bars ? (
        <>
          <div className="flex items-center gap-2">
            <Text type="secondary" className="text-[10px] w-10 flex-shrink-0">
              Before
            </Text>
            <Progress
              percent={bars.beforePct}
              showInfo={false}
              size="small"
              strokeColor="#D4D4D8"
              trailColor="#F4F4F5"
              className="!mb-0 flex-1"
            />
            <Text type="secondary" className="text-[12px] w-[68px] flex-shrink-0 text-right">
              {formatMetricValue(metric.unit, metric.before)}
            </Text>
          </div>
          <div className="flex items-center gap-2">
            <Text className="text-[10px] w-10 flex-shrink-0 font-medium" style={{ color: color.text }}>
              After
            </Text>
            <Progress
              percent={bars.afterPct}
              showInfo={false}
              size="small"
              strokeColor={color.bar}
              trailColor="#F4F4F5"
              className="!mb-0 flex-1"
            />
            <Text className="text-[13px] font-semibold w-[68px] flex-shrink-0 text-right" style={{ color: color.text }}>
              {formatMetricValue(metric.unit, metric.after)}
            </Text>
          </div>
        </>
      ) : metric.unit === "boolean" ? (
        <div className="flex items-center gap-1.5">
          <Tag
            style={{ margin: 0, fontSize: 11, background: "#FAFAFA", color: "#71717A", border: "1px solid #E4E4E7" }}
          >
            {formatMetricValue("boolean", metric.before)}
          </Tag>
          <span className="text-gray-300 text-[11px]">→</span>
          <Tag
            style={{
              margin: 0,
              fontSize: 11,
              background:
                direction === "neutral"
                  ? "#FAFAFA"
                  : direction === "improved"
                    ? ACCEPTANCE_COLOR.High.bg
                    : ACCEPTANCE_COLOR.Low.bg,
              color: direction === "neutral" ? "#71717A" : color.text,
              border: `1px solid ${direction === "neutral" ? "#E4E4E7" : color.text}`,
            }}
          >
            {formatMetricValue("boolean", metric.after)}
          </Tag>
        </div>
      ) : (
        <div className="flex items-baseline gap-2">
          <Text delete type="secondary" className="text-[13px]">
            {formatMetricValue(metric.unit, metric.before)}
          </Text>
          <Text className="text-[15px] font-bold" style={{ color: color.text }}>
            {formatMetricValue(metric.unit, metric.after)}
          </Text>
        </div>
      )}
    </div>
  );
}

/** Legacy client_gain shape from older saved-custom records — deprecated,
 *  shown here only as a simplified plain-number fallback (no before/after). */
function LegacyGainFallback({ gain }: { gain: T_ClientGainLegacy }) {
  return (
    <div className="flex flex-col gap-2">
      {gain.dollar_savings != null && (
        <div className="flex justify-between">
          <Text type="secondary" className="text-xs">
            Dollar savings
          </Text>
          <Text className="text-[13px] font-semibold">{formatMoney(gain.dollar_savings)}</Text>
        </div>
      )}
      {gain.cash_flow_relief_per_period != null && (
        <div className="flex justify-between">
          <Text type="secondary" className="text-xs">
            Cash flow relief / period
          </Text>
          <Text className="text-[13px] font-semibold">{formatMoney(gain.cash_flow_relief_per_period)}</Text>
        </div>
      )}
      {gain.time_change_weeks != null && (
        <div className="flex justify-between">
          <Text type="secondary" className="text-xs">
            Time change
          </Text>
          <Text className="text-[13px] font-semibold">{gain.time_change_weeks} wks</Text>
        </div>
      )}
    </div>
  );
}
